import React, {
  useState,
  createContext,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react'
import firebase from 'firebase/compat/app'
import { listenToUser } from '@services/UserService'
import { auth } from '@utils/firebase'
import { AuthContextProps, AuthContextDefaultValues } from '@Interfaces/Context/AuthInterface'
import { CurrentAuth, User } from '@Interfaces/Data/UserInterface'
import { ContextComponentProps } from '@Interfaces/Util/CommonInterface'

export const AuthContext = createContext<AuthContextProps>(AuthContextDefaultValues)


export const AuthProvider = ({ children }: ContextComponentProps) => {
  const [initializing, setInitializing] = useState(true)
  const [currentAuth, setCurrentAuth] = useState<CurrentAuth | null>(null)
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [onboarding, setOnboarding] = useState(false)
  const [queryParams, setQueryParams] = useState<URLSearchParams | null>(null)
  const [newUser, setNewUser] = useState<User | null>(null)

  // Handle user state changes (memoized callback)
  const onAuthStateChanged = useCallback(
    (firebaseUser: firebase.User | null) => {
      const nextAuth: CurrentAuth | null =
        firebaseUser !== null
          ? { uid: firebaseUser.uid, email: firebaseUser.email ?? undefined }
          : null
      setCurrentAuth(nextAuth)
      if (initializing) setInitializing(false)
    },
    [initializing]
  )

  useEffect(() => {
    const subscriber = auth.onAuthStateChanged(onAuthStateChanged)
    return () => {
      setOnboarding(false)
      subscriber() // unsubscribe on unmount
    }
  }, [initializing, onAuthStateChanged])

  useEffect(() => {
    if (!currentAuth) {
      return setOnboarding(false)
    }

    const userSubscriber = listenToUser(currentAuth, setCurrentUser)

    return () => {
      setCurrentUser(null)
      userSubscriber()
    }
  }, [currentAuth])

  const contextValue = useMemo(
    () => ({
      initializing,
      currentAuth,
      setCurrentAuth,
      currentUser,
      setCurrentUser,
      onboarding,
      setOnboarding,
      queryParams,
      setQueryParams,
      newUser,
      setNewUser,
    }),
    [initializing, currentAuth, currentUser, onboarding, queryParams, newUser]
  )

  if (initializing) return null

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextProps => useContext(AuthContext)
